/**
 * ContentQueue
 *
 * Filterable queue table for the Content Review Dashboard.
 * Displays content items with type, title, site, created date, status, and actions.
 *
 * Part of Story 29: Content Review Dashboard with Reviewer Attribution
 *
 * Date: December 16, 2025
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FileText,
  Tag,
  FolderTree,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  CheckCircle2,
  XCircle,
  HelpCircle,
  AlertTriangle,
  Archive
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { format } from 'date-fns';
import { QueueItem, getContentTypeLabel, getStatusLabel } from '@/services/contentReviewService';

type SortableColumn = 'updated_at' | 'title' | 'content_type';
type SortOrder = 'ASC' | 'DESC';

interface ContentQueueProps {
  items: QueueItem[];
  loading: boolean;
  onReview: (item: QueueItem) => void;
  pagination: {
    page: number;
    totalPages: number;
    total: number;
    limit: number;
  };
  onPageChange: (page: number) => void;
  selectedItems?: Set<string>;
  /**
   * ⛔ `mode` distinguishes the two mutations this component performs, because the resulting Set
   * cannot: `handleSelectAll` REPLACES the selection with this page's ids, while `handleSelectItem`
   * copies the existing set and toggles one. A consumer tracking which filter a selection belongs to
   * needs to know whether the incoming set carried the previous one forward — inferring it from the
   * ids is ambiguous when the two overlap. Optional, so existing callers are unaffected.
   */
  onSelectionChange?: (selectedIds: Set<string>, mode?: 'replace' | 'toggle') => void;
  showSelection?: boolean;
  sortBy?: SortableColumn;
  sortOrder?: SortOrder;
  onSortChange?: (column: SortableColumn) => void;
}

function getContentTypeIcon(type: string) {
  switch (type) {
    case 'ai_article':
      return <FileText className="h-4 w-4 text-blue-500" />;
    case 'product_aeo':
      return <Tag className="h-4 w-4 text-green-500" />;
    case 'category_aeo':
      return <FolderTree className="h-4 w-4 text-purple-500" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
}

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'approved':
      return 'default';
    case 'rejected':
      return 'destructive';
    case 'pending':
    default:
      return 'secondary';
  }
}

function getFaqQaDisplay(faqQaPassed: boolean | null, faqCount: number | null) {
  if (faqQaPassed === true) {
    return {
      icon: <CheckCircle2 className="h-4 w-4 text-green-500" />,
      label: `FAQ OK (${faqCount ?? 0})`,
      variant: 'default' as const
    };
  } else if (faqQaPassed === false) {
    return {
      icon: <XCircle className="h-4 w-4 text-red-500" />,
      label: 'FAQ Failed',
      variant: 'destructive' as const
    };
  }
  return {
    icon: <HelpCircle className="h-4 w-4 text-gray-400" />,
    label: 'No FAQ',
    variant: 'secondary' as const
  };
}

/**
 * task-pu60uzfmq2 — the generation-time consistency detector, made VISIBLE.
 *
 * The detector has always run and stamped its findings on the page; nothing read them, so a page whose own
 * detector recorded a HIGH contradiction — on a supplements store, a fabricated allergen claim — looked
 * identical in this queue to a clean one. Measured: 4 of 11 pages on pagemind-vitamins-and-supplements.
 *
 * ⛔ THIS IS NOT A BLOCK AND MUST NOT READ AS ONE. Whether a HIGH finding should stop a page entering review
 * is a product decision that has not been taken. This tells the human gate what the machine already knew.
 * ⛔ AND IT IS DELIBERATELY NOT DRIVEN BY THE `consistency_blocked` FLAG, which is false on every measured
 * case by design (it needs 3+ violations or a 'critical'). A badge driven by that flag would be absent on
 * exactly the pages that motivated this, which is worse than no badge: it would read as a clean check.
 */
function getConsistencyDisplay(item: Pick<QueueItem, 'consistencyViolationCount' | 'consistencyMaxSeverity' | 'consistencyViolations'>) {
  const count = item.consistencyViolationCount ?? 0;
  if (count < 1) return null;
  const severity = (item.consistencyMaxSeverity || '').toLowerCase();
  const severe = severity === 'critical' || severity === 'high';
  const label = `${count} consistency ${count === 1 ? 'issue' : 'issues'}${severity ? ` (${severity})` : ''}`;
  // the reviewer needs the TEXT, not a badge — the whole point is that the contradiction is readable
  const details = (item.consistencyViolations || [])
    .map((v) => [v.severity ? `[${v.severity}]` : null, v.message].filter(Boolean).join(' '))
    .filter((line) => line.length > 0);
  // ⚠️ THE TEXT MUST NOT BE HOVER-ONLY. The tooltip below matches the existing needs_manual_review idiom,
  // but a Radix tooltip renders its content only on hover — so a reviewer scanning the queue would see a
  // badge and never learn WHAT the contradiction is, and a screen reader would get the icon alone. The row
  // asked for the page to be "visibly flagged ... with the violation text", so the first message goes into
  // the ACCESSIBLE NAME, where it is reachable without hovering, and the tooltip carries the full list.
  const accessibleName = details.length > 0 ? `${label}: ${details[0]}` : label;
  return {
    severe,
    label,
    accessibleName,
    details,
    icon: <AlertTriangle className={severe ? 'h-4 w-4 text-red-600' : 'h-4 w-4 text-amber-500'} />,
  };
}

function getManualReviewDisplay(needsManualReview: boolean) {
  if (needsManualReview) {
    return {
      icon: <AlertTriangle className="h-4 w-4 text-orange-500" />,
      label: 'Needs Review',
      tooltip: 'Suspicious data detected - requires manual verification'
    };
  }
  return null;
}

// Helper component for sortable column header
function SortableHeader({
  column,
  label,
  currentSort,
  currentOrder,
  onSort,
  className = ''
}: {
  column: SortableColumn;
  label: string;
  currentSort?: SortableColumn;
  currentOrder?: SortOrder;
  onSort?: (column: SortableColumn) => void;
  className?: string;
}) {
  const isActive = currentSort === column;

  return (
    <button
      onClick={() => onSort?.(column)}
      className={`flex items-center gap-1 hover:text-foreground transition-colors ${
        isActive ? 'text-foreground font-medium' : 'text-muted-foreground'
      } ${className}`}
      disabled={!onSort}
    >
      {label}
      {onSort && (
        <span className="w-4 h-4 flex items-center justify-center">
          {isActive ? (
            currentOrder === 'ASC' ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )
          ) : (
            <ChevronsUpDown className="h-3 w-3 opacity-50" />
          )}
        </span>
      )}
    </button>
  );
}

export function ContentQueue({
  items,
  loading,
  onReview,
  pagination,
  onPageChange,
  selectedItems = new Set(),
  onSelectionChange,
  showSelection = false,
  sortBy,
  sortOrder,
  onSortChange
}: ContentQueueProps) {
  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      const allIds = new Set(items.map(item => `${item.contentType}:${item.id}`));
      onSelectionChange(allIds, 'replace');
    } else {
      onSelectionChange(new Set(), 'replace');
    }
  };

  const handleSelectItem = (item: QueueItem, checked: boolean) => {
    if (!onSelectionChange) return;
    const itemKey = `${item.contentType}:${item.id}`;
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemKey);
    } else {
      newSelected.delete(itemKey);
    }
    onSelectionChange(newSelected, 'toggle');
  };

  const allSelected = items.length > 0 && items.every(item =>
    selectedItems.has(`${item.contentType}:${item.id}`)
  );
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                {showSelection && <TableHead className="w-[50px]"></TableHead>}
                <TableHead className="w-[100px]">Type</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Site</TableHead>
                <TableHead className="w-[120px]">Updated</TableHead>
                <TableHead className="w-[150px]">Category</TableHead>
                <TableHead className="w-[100px]">FAQ QA</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  {showSelection && <TableCell><Skeleton className="h-4 w-4" /></TableCell>}
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-16" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">No content items match your filters.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              {showSelection && (
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                  />
                </TableHead>
              )}
              <TableHead className="w-[120px]">
                <SortableHeader
                  column="content_type"
                  label="Type"
                  currentSort={sortBy}
                  currentOrder={sortOrder}
                  onSort={onSortChange}
                />
              </TableHead>
              <TableHead>
                <SortableHeader
                  column="title"
                  label="Title"
                  currentSort={sortBy}
                  currentOrder={sortOrder}
                  onSort={onSortChange}
                />
              </TableHead>
              <TableHead className="w-[150px]">Site</TableHead>
              <TableHead className="w-[120px]">
                <SortableHeader
                  column="updated_at"
                  label="Updated"
                  currentSort={sortBy}
                  currentOrder={sortOrder}
                  onSort={onSortChange}
                />
              </TableHead>
              <TableHead className="w-[150px]">Category</TableHead>
              <TableHead className="w-[100px]">FAQ QA</TableHead>
              <TableHead className="w-[120px]">Status</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const faqDisplay = getFaqQaDisplay(item.faqQaPassed, item.faqCount);
              const manualReviewDisplay = getManualReviewDisplay(item.needsManualReview);
              const consistencyDisplay = getConsistencyDisplay(item);
              const itemKey = `${item.contentType}:${item.id}`;
              return (
                <TableRow key={item.id}>
                  {showSelection && (
                    <TableCell>
                      <Checkbox
                        checked={selectedItems.has(itemKey)}
                        onCheckedChange={(checked) => handleSelectItem(item, checked as boolean)}
                        aria-label={`Select ${item.title}`}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getContentTypeIcon(item.contentType)}
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        {getContentTypeLabel(item.contentType)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt=""
                          loading="lazy"
                          className="w-7 h-7 object-contain rounded shrink-0"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <div className="max-w-[280px] truncate" title={item.title}>
                        {item.title}
                      </div>
                      {/* S3: the SKU was ALREADY in this payload (content-review.js:816/844) and
                          dropped at the type boundary, so the queue showed names only. Names are
                          not unique — this is the identifier an operator can actually target when
                          selecting a cohort to regenerate. Same treatment as F1's offering list. */}
                      {item.sku && (
                        <div className="text-xs font-mono text-muted-foreground select-text break-all">
                          SKU {item.sku}
                        </div>
                      )}
                      {item.isInactive && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center">
                                <Archive className="h-4 w-4 text-gray-400" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[300px]">
                              <p className="font-medium text-gray-600">Inactive Product</p>
                              <p className="text-sm mt-1">This product is no longer in the active feed</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {consistencyDisplay && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {/* ⛔ VISIBLE TEXT AND A FOCUSABLE CONTROL — not an icon with an aria-label.
                                  Round 2 shipped the icon plus an accessible name, which helped a screen
                                  reader and nobody else: a sighted reviewer scanning the queue still saw only
                                  a coloured triangle, and the trigger was a non-focusable div so a
                                  keyboard-only reviewer could never open the tooltip at all. Both legs said
                                  so. The severity and the first message are now RENDERED, and the trigger is
                                  a real button, so the finding is readable without a mouse and without hover. */}
                              <button
                                type="button"
                                className="flex items-center gap-1 text-left focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500 rounded"
                                aria-label={consistencyDisplay.accessibleName}
                              >
                                {consistencyDisplay.icon}
                                <span className={consistencyDisplay.severe ? 'text-xs font-medium text-red-600' : 'text-xs font-medium text-amber-600'}>
                                  {consistencyDisplay.label}
                                </span>
                                {consistencyDisplay.details[0] && (
                                  <span className="text-xs text-muted-foreground max-w-[220px] truncate">
                                    {consistencyDisplay.details[0]}
                                  </span>
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[360px]">
                              <p className={consistencyDisplay.severe ? 'font-medium text-red-600' : 'font-medium text-amber-600'}>
                                {consistencyDisplay.label}
                              </p>
                              {consistencyDisplay.details.map((line, i) => (
                                <p key={i} className="text-sm mt-1">{line}</p>
                              ))}
                              <p className="text-xs mt-2 opacity-70">Detected at generation time. This does not block approval.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {manualReviewDisplay && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center">
                                {manualReviewDisplay.icon}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[300px]">
                              <p className="font-medium text-orange-500">{manualReviewDisplay.label}</p>
                              <p className="text-sm mt-1">{manualReviewDisplay.tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {item.previousRejection && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 text-orange-600">
                                <AlertTriangle className="h-4 w-4" />
                                {item.previousRejection.totalRejections > 1 && (
                                  <span className="text-xs font-medium">
                                    {item.previousRejection.totalRejections}x
                                  </span>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[300px]">
                              <p className="font-medium text-orange-600">Previously Rejected</p>
                              <p className="text-sm mt-1">{item.previousRejection.reason}</p>
                              {item.previousRejection.rejectedBy && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  By {item.previousRejection.rejectedBy}
                                </p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {item.siteName}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {item.updatedAt ? format(new Date(item.updatedAt), 'MMM d, HH:mm') : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {item.category ? (
                      <span
                        className="text-xs text-muted-foreground max-w-[140px] truncate block"
                        title={item.category}
                      >
                        {item.category.split(' > ').pop()}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1" title={faqDisplay.label}>
                      {faqDisplay.icon}
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        {faqDisplay.label}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(item.status)}>
                      {getStatusLabel(item.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onReview(item)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} items
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentQueue;
